import { useEffect, useRef, useState } from "react";
import API from "../api";
import { Filesystem, Directory } from "@capacitor/filesystem";
import { Capacitor } from "@capacitor/core";
import { Preferences } from "@capacitor/preferences";
import { FilePicker } from "@capawesome/capacitor-file-picker";

import bgTemple from "../assets/bg-temple.png";

import ouvertureIcon from "../assets/icons/ouverture.png";
import plancheIcon from "../assets/icons/planche1.png";
import unionIcon from "../assets/icons/union.png";
import troncIcon from "../assets/icons/tronc.png";
import fermetureIcon from "../assets/icons/fermeture.png";
import autreIcon from "../assets/icons/autre.png";

function Music() {
  const user = JSON.parse(localStorage.getItem("user") || "{}");
  const isAdmin = user.role === "admin";

  const audioRef = useRef(null);

  const [toast, setToast] = useState("");
  const [tracks, setTracks] = useState([]);
  const [currentTrack, setCurrentTrack] = useState(null);
  const [currentIndex, setCurrentIndex] = useState(-1);
  const [activeCategory, setActiveCategory] = useState("ouverture");

  const [isPlaying, setIsPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [currentTime, setCurrentTime] = useState("0:00");
  const [duration, setDuration] = useState("0:00");

  const [title, setTitle] = useState("");
  const [category, setCategory] = useState("ouverture");
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [offlineTracks, setOfflineTracks] = useState({});

  const categories = [
    { key: "ouverture", label: "Ouverture", icon: ouvertureIcon },
    { key: "planche", label: "Planche", icon: plancheIcon },
    { key: "union", label: "Union", icon: unionIcon },
    { key: "tronc", label: "Tronc", icon: troncIcon },
    { key: "fermeture", label: "Fermeture", icon: fermetureIcon },
    { key: "autre", label: "Autre", icon: autreIcon },
  ];

  useEffect(() => {
    fetchMusic();
  }, []);

  const activeCat = categories.find((cat) => cat.key === activeCategory);

  const showToast = (message) => {
    setToast(message);
    setTimeout(() => setToast(""), 2500);
  };

  const getCategoryIcon = (key) => {
    return categories.find((cat) => cat.key === key)?.icon || autreIcon;
  };

  const getCategoryLabel = (key) => {
    return categories.find((cat) => cat.key === key)?.label || "Musique";
  };

  const getMusicUrl = (track) => {
    return `${API.defaults.baseURL}/uploads/music/${encodeURIComponent(
      track.filename
    )}`;
  };

  const formatTime = (seconds) => {
    if (!seconds || isNaN(seconds)) return "0:00";
    const min = Math.floor(seconds / 60);
    const sec = Math.floor(seconds % 60);
    return `${min}:${sec < 10 ? "0" : ""}${sec}`;
  };

  const pickAudioFile = async () => {
    try {
      const result = await FilePicker.pickFiles({
        types: ["audio/*"],
        multiple: false,
        readData: true,
      });

      const picked = result.files?.[0];
      if (!picked) return;

      if (!picked.data) {
        alert("Impossible de lire le fichier sélectionné");
        return;
      }

      const byteCharacters = atob(picked.data);
      const byteNumbers = new Array(byteCharacters.length);

      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }

      const blob = new Blob([new Uint8Array(byteNumbers)], {
        type: picked.mimeType || "audio/mpeg",
      });

      const selectedFile = new File([blob], picked.name || "musique.mp3", {
        type: picked.mimeType || "audio/mpeg",
      });

      setFile(selectedFile);
      showToast("Fichier sélectionné ✅");
    } catch (err) {
      console.error("Erreur sélection fichier:", err);
      alert("Impossible de choisir le fichier");
    }
  };

  const fetchMusic = async () => {
    try {
      const res = await API.get("/music");
      setTracks(res.data);

      await Preferences.set({
        key: "offline_music_tracks",
        value: JSON.stringify(res.data),
      });

      await checkOfflineTracks(res.data);
    } catch (err) {
      console.log("Mode hors ligne : chargement local");

      const saved = await Preferences.get({ key: "offline_music_tracks" });

      if (saved.value) {
        const localTracks = JSON.parse(saved.value);
        setTracks(localTracks);
        await checkOfflineTracks(localTracks);
      }
    }
  };

  const checkOfflineTracks = async (list) => {
    const status = {};

    for (const track of list) {
      try {
        await Filesystem.stat({
          path: `music/${track.filename}`,
          directory: Directory.Data,
        });

        status[track.id] = true;
      } catch {
        status[track.id] = false;
      }
    }

    setOfflineTracks(status);
  };

  const downloadOneTrack = async (track) => {
    const response = await fetch(getMusicUrl(track));
    const blob = await response.blob();

    const reader = new FileReader();

    await new Promise((resolve, reject) => {
      reader.onloadend = async () => {
        try {
          const base64Data = reader.result.split(",")[1];

          await Filesystem.writeFile({
            path: `music/${track.filename}`,
            data: base64Data,
            directory: Directory.Data,
            recursive: true,
          });

          resolve();
        } catch (err) {
          reject(err);
        }
      };

      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  };

  const syncOfflineMusic = async () => {
    try {
      setLoading(true);

      const res = await API.get("/music");
      const serverTracks = res.data;

      await Preferences.set({
        key: "offline_music_tracks",
        value: JSON.stringify(serverTracks),
      });

      for (const track of serverTracks) {
        try {
          await Filesystem.stat({
            path: `music/${track.filename}`,
            directory: Directory.Data,
          });
        } catch {
          await downloadOneTrack(track);
        }
      }

      setTracks(serverTracks);
      await checkOfflineTracks(serverTracks);

      showToast("Planche musicale mise à jour ✅");
    } catch (err) {
      console.error("Erreur synchronisation musique:", err);
      alert("Erreur pendant la mise à jour");
    } finally {
      setLoading(false);
    }
  };

  const uploadMusic = async () => {
    if (!title.trim()) {
      alert("Titre obligatoire");
      return;
    }

    if (!file) {
      alert("Fichier audio obligatoire");
      return;
    }

    try {
      setLoading(true);

      const formData = new FormData();
      formData.append("title", title.trim());
      formData.append("category", category);
      formData.append("file", file);

      await API.post("/music", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      setTitle("");
      setCategory("ouverture");
      setFile(null);

      await fetchMusic();

      alert("Musique ajoutée ✅");
    } catch (err) {
      console.error("Erreur upload musique:", err);
      alert(
        "Erreur upload : " +
          (err.response?.status || "") +
          " " +
          JSON.stringify(err.response?.data || err.message)
      );
    } finally {
      setLoading(false);
    }
  };

  const downloadOffline = async (track) => {
    try {
      setLoading(true);

      await downloadOneTrack(track);

      setOfflineTracks((prev) => ({
        ...prev,
        [track.id]: true,
      }));

      alert("Musique disponible hors ligne ✅");
    } catch (err) {
      console.error("Erreur téléchargement offline:", err);
      alert("Erreur téléchargement offline");
    } finally {
      setLoading(false);
    }
  };

  const getTrackSrc = async (track) => {
    let src = getMusicUrl(track);

    if (offlineTracks[track.id]) {
      try {
        const fileUri = await Filesystem.getUri({
          path: `music/${track.filename}`,
          directory: Directory.Data,
        });

        src = Capacitor.convertFileSrc(fileUri.uri);
      } catch {
        src = getMusicUrl(track);
      }
    }

    return src;
  };

  const playTrack = async (track) => {
    try {
      const src = await getTrackSrc(track);
      const index = tracks.findIndex((t) => t.id === track.id);

      setCurrentTrack(track);
      setCurrentIndex(index);

      setTimeout(() => {
        if (audioRef.current) {
          audioRef.current.src = src;
          audioRef.current.play();
          setIsPlaying(true);
        }
      }, 100);
    } catch (err) {
      console.error("Erreur lecture:", err);
    }
  };

  const togglePlayPause = () => {
    if (!audioRef.current) return;

    if (audioRef.current.paused) {
      audioRef.current.play();
      setIsPlaying(true);
    } else {
      audioRef.current.pause();
      setIsPlaying(false);
    }
  };

  const playNext = () => {
    if (tracks.length === 0) return;
    const nextIndex = currentIndex + 1 >= tracks.length ? 0 : currentIndex + 1;
    playTrack(tracks[nextIndex]);
  };

  const playPrevious = () => {
    if (tracks.length === 0) return;
    const prevIndex = currentIndex - 1 < 0 ? tracks.length - 1 : currentIndex - 1;
    playTrack(tracks[prevIndex]);
  };

  const handleTimeUpdate = () => {
    if (!audioRef.current) return;

    const audio = audioRef.current;
    const percent = audio.duration
      ? (audio.currentTime / audio.duration) * 100
      : 0;

    setProgress(percent);
    setCurrentTime(formatTime(audio.currentTime));
    setDuration(formatTime(audio.duration));
  };

  const handleSeek = (e) => {
    if (!audioRef.current) return;

    const value = Number(e.target.value);
    const audio = audioRef.current;

    if (audio.duration) {
      audio.currentTime = (value / 100) * audio.duration;
      setProgress(value);
    }
  };

  const deleteTrack = async (id) => {
    if (!window.confirm("Supprimer cette musique ?")) return;

    try {
      await API.delete(`/music/${id}`);
      await fetchMusic();
    } catch (err) {
      console.error("Erreur suppression:", err);
      alert("Erreur suppression");
    }
  };

  const filteredTracks = tracks.filter(
    (track) => track.category === activeCategory
  );

  return (
    <div style={styles.page}>
      <div style={styles.overlay} />

      <div style={styles.content}>
        <div style={styles.header}>
          <div>
            <div style={styles.kicker}>Planche musicale</div>
            <h1 style={styles.title}>Musique</h1>
            <div style={styles.subtitle}>Musique de secours hors ligne</div>
          </div>

          <button
            type="button"
            style={styles.syncButton}
            onClick={syncOfflineMusic}
            disabled={loading}
          >
            🔄 {loading ? "Maj..." : "Maj"}
          </button>
        </div>

        {isAdmin && (
          <div style={styles.uploadBox}>
            <div style={styles.uploadTitle}>Ajouter une musique</div>

            <input
              style={styles.input}
              type="text"
              placeholder="Titre de la musique"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
            />

            <select
              style={styles.input}
              value={category}
              onChange={(e) => setCategory(e.target.value)}
            >
              {categories.map((cat) => (
                <option key={cat.key} value={cat.key}>
                  {cat.label}
                </option>
              ))}
            </select>

            <button
              type="button"
              style={styles.fileButton}
              onClick={pickAudioFile}
              disabled={loading}
            >
              {file ? `🎵 ${file.name}` : "Choisir un fichier audio"}
            </button>

            <button
              type="button"
              style={styles.uploadButton}
              onClick={uploadMusic}
              disabled={loading}
            >
              {loading ? "Envoi..." : "Ajouter la musique"}
            </button>
          </div>
        )}

        <div style={styles.grid}>
          {categories.map((cat) => {
            const count = tracks.filter((t) => t.category === cat.key).length;
            const active = activeCategory === cat.key;

            return (
              <button
                type="button"
                key={cat.key}
                style={{
                  ...styles.gridCard,
                  ...(active ? styles.gridCardActive : {}),
                }}
                onClick={() => setActiveCategory(cat.key)}
              >
                <img src={cat.icon} alt={cat.label} style={styles.gridIcon} />
                <div style={styles.gridLabel}>{cat.label}</div>
                <div style={styles.gridCount}>{count}</div>
              </button>
            );
          })}
        </div>

        <div style={styles.sectionTitleRow}>
          <div style={styles.sectionLeft}>
            <img
              src={activeCat?.icon}
              alt={activeCat?.label}
              style={styles.sectionIconImg}
            />
            <h2 style={styles.sectionTitle}>{activeCat?.label}</h2>
          </div>

          <div style={styles.countBadge}>
            {filteredTracks.length} titre{filteredTracks.length > 1 ? "s" : ""}
          </div>
        </div>

        {filteredTracks.length === 0 && (
          <div style={styles.empty}>
            <img src={activeCat?.icon} alt="" style={styles.emptyIconImg} />
            <div>Aucune musique dans cette catégorie.</div>
          </div>
        )}

        {filteredTracks.map((track) => (
          <div
            key={track.id}
            style={{
              ...styles.card,
              ...(currentTrack?.id === track.id ? styles.cardActive : {}),
            }}
          >
            <div style={styles.left}>
              <div style={styles.trackIconCircle}>
                <img
                  src={getCategoryIcon(track.category)}
                  alt=""
                  style={styles.trackIconImg}
                />
              </div>

              <div style={styles.trackText}>
                <div style={styles.trackTitle}>{track.title}</div>
                <div style={styles.trackInfo}>
                  {offlineTracks[track.id]
                    ? "Disponible hors ligne ✅"
                    : "Non téléchargée"}
                </div>
              </div>
            </div>

            <div style={styles.actions}>
              <button
                type="button"
                style={styles.playButton}
                onClick={() => playTrack(track)}
              >
                ▶
              </button>

              {!offlineTracks[track.id] && (
                <button
                  type="button"
                  style={styles.downloadButton}
                  onClick={() => downloadOffline(track)}
                >
                  ⬇
                </button>
              )}

              {isAdmin && (
                <button
                  type="button"
                  style={styles.deleteButton}
                  onClick={() => deleteTrack(track.id)}
                >
                  ×
                </button>
              )}
            </div>
          </div>
        ))}
      </div>

      <audio
        ref={audioRef}
        onTimeUpdate={handleTimeUpdate}
        onLoadedMetadata={handleTimeUpdate}
        onEnded={playNext}
        onPause={() => setIsPlaying(false)}
        onPlay={() => setIsPlaying(true)}
      />

      {currentTrack && (
        <div style={styles.player}>
          <div style={styles.playerMain}>
            <div style={styles.playerCover}>
              <img
                src={getCategoryIcon(currentTrack.category)}
                alt=""
                style={styles.playerCoverImg}
              />
            </div>

            <div style={styles.playerText}>
              <div style={styles.playerTitle}>{currentTrack.title}</div>
              <div style={styles.playerCategory}>
                {getCategoryLabel(currentTrack.category)}
              </div>
            </div>

            <div style={styles.playerControls}>
              <button type="button" style={styles.smallControl} onClick={playPrevious}>
                ᐊ
              </button>

              <button type="button" style={styles.bigControl} onClick={togglePlayPause}>
                {isPlaying ? "Ⅱ" : "▶"}
              </button>

              <button type="button" style={styles.smallControl} onClick={playNext}>
                ᐅ
              </button>
            </div>
          </div>

          <div style={styles.timeLine}>
            <span>{currentTime}</span>
            <span>{duration}</span>
          </div>

          <input
            type="range"
            min="0"
            max="100"
            value={progress}
            onChange={handleSeek}
            style={styles.progress}
          />
        </div>
      )}

      {toast && <div style={styles.toast}>{toast}</div>}
    </div>
  );
}

const styles = {
  toast: {
    position: "fixed",
    left: "24px",
    right: "24px",
    bottom: "170px",
    zIndex: 999,
    padding: "14px 16px",
    borderRadius: "18px",
    background: "rgba(5,10,18,0.96)",
    border: "1px solid rgba(214,181,109,0.45)",
    color: "#f5ead8",
    fontSize: "15px",
    fontWeight: "800",
    textAlign: "center",
  },

  page: {
    minHeight: "100vh",
    position: "relative",
    overflowX: "hidden",
    backgroundImage: `url(${bgTemple})`,
    backgroundSize: "cover",
    backgroundPosition: "center top",
    color: "#f8f4ec",
    fontFamily: "Arial, sans-serif",
  },

  overlay: {
    position: "fixed",
    inset: 0,
    background:
      "linear-gradient(180deg, rgba(3,7,14,0.42) 0%, rgba(3,7,14,0.82) 45%, rgba(3,7,14,0.96) 100%)",
    zIndex: 0,
  },

  content: {
    position: "relative",
    zIndex: 1,
    padding: "24px 18px 220px",
  },

  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    marginBottom: "18px",
    gap: "12px",
  },

  kicker: {
    color: "#d6b56d",
    fontSize: "16px",
    fontWeight: "900",
  },

  title: {
    margin: 0,
    color: "#ffffff",
    fontSize: "42px",
    lineHeight: "42px",
    fontWeight: "900",
  },

  subtitle: {
    marginTop: "6px",
    color: "rgba(255,255,255,0.72)",
    fontSize: "17px",
  },

  syncButton: {
    padding: "8px 12px",
    borderRadius: "12px",
    border: "1px solid rgba(214,181,109,0.35)",
    background: "rgba(0,0,0,0.35)",
    color: "#d6b56d",
    fontSize: "13px",
    fontWeight: "800",
  },

  uploadBox: {
    marginBottom: "18px",
    padding: "15px",
    borderRadius: "20px",
    background: "rgba(1,6,13,0.72)",
    border: "1px solid rgba(214,181,109,0.42)",
  },

  uploadTitle: {
    color: "#d6b56d",
    fontSize: "18px",
    fontWeight: "900",
    marginBottom: "12px",
  },

  input: {
    width: "100%",
    padding: "13px",
    marginBottom: "10px",
    borderRadius: "13px",
    border: "1px solid rgba(214,181,109,0.28)",
    background: "rgba(255,255,255,0.08)",
    color: "#ffffff",
    fontSize: "15px",
    boxSizing: "border-box",
  },

  fileButton: {
    width: "100%",
    padding: "14px",
    marginBottom: "12px",
    borderRadius: "14px",
    border: "1px solid rgba(214,181,109,0.45)",
    background: "rgba(0,0,0,0.35)",
    color: "#d6b56d",
    fontSize: "15px",
    fontWeight: "900",
    textAlign: "center",
  },

  uploadButton: {
    width: "100%",
    padding: "14px",
    borderRadius: "14px",
    border: "none",
    background: "#d6b56d",
    color: "#060606",
    fontSize: "15px",
    fontWeight: "900",
  },

  grid: {
    display: "grid",
    gridTemplateColumns: "repeat(3, 1fr)",
    gap: "10px",
    marginBottom: "20px",
  },

  gridCard: {
    minHeight: "86px",
    padding: "9px 6px",
    borderRadius: "16px",
    border: "1px solid rgba(214,181,109,0.34)",
    background:
      "linear-gradient(180deg, rgba(4,10,20,0.78), rgba(0,2,8,0.88))",
    color: "#d6b56d",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    position: "relative",
  },

  gridCardActive: {
    background:
      "linear-gradient(180deg, rgba(244,214,107,0.95), rgba(199,155,37,0.95))",
    color: "#050505",
  },

  gridIcon: {
    width: "38px",
    height: "38px",
    objectFit: "contain",
    marginBottom: "5px",
  },

  gridLabel: {
    fontSize: "11px",
    fontWeight: "900",
    textAlign: "center",
  },

  gridCount: {
    position: "absolute",
    top: "5px",
    right: "6px",
    minWidth: "18px",
    height: "18px",
    borderRadius: "999px",
    background: "rgba(0,0,0,0.55)",
    color: "#d6b56d",
    fontSize: "10px",
    fontWeight: "900",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },

  sectionTitleRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: "13px",
  },

  sectionLeft: {
    display: "flex",
    alignItems: "center",
    gap: "12px",
  },

  sectionIconImg: {
    width: "38px",
    height: "38px",
    objectFit: "contain",
  },

  sectionTitle: {
    margin: 0,
    color: "#d6b56d",
    fontSize: "30px",
    fontWeight: "900",
  },

  countBadge: {
    padding: "8px 12px",
    borderRadius: "14px",
    color: "#d6b56d",
    fontSize: "14px",
    fontWeight: "900",
    border: "1px solid rgba(214,181,109,0.5)",
    background: "rgba(0,0,0,0.35)",
  },

  empty: {
    padding: "24px",
    textAlign: "center",
    borderRadius: "20px",
    background: "rgba(1,6,13,0.68)",
    border: "1px solid rgba(214,181,109,0.28)",
  },

  emptyIconImg: {
    width: "56px",
    height: "56px",
    objectFit: "contain",
    marginBottom: "8px",
  },

  card: {
    minHeight: "76px",
    padding: "12px",
    marginBottom: "11px",
    borderRadius: "17px",
    background:
      "linear-gradient(180deg, rgba(2,8,17,0.9), rgba(0,3,9,0.92))",
    border: "1px solid rgba(214,181,109,0.36)",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "10px",
  },

  cardActive: {
    border: "1px solid rgba(246,213,107,0.95)",
  },

  left: {
    display: "flex",
    alignItems: "center",
    gap: "12px",
    flex: 1,
    minWidth: 0,
  },

  trackIconCircle: {
    width: "54px",
    height: "54px",
    minWidth: "54px",
    borderRadius: "50%",
    border: "2px solid rgba(246,213,107,0.9)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },

  trackIconImg: {
    width: "38px",
    height: "38px",
    objectFit: "contain",
  },

  trackText: {
    flex: 1,
    minWidth: 0,
  },

  trackTitle: {
    color: "#ffffff",
    fontSize: "19px",
    fontWeight: "900",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },

  trackInfo: {
    color: "rgba(255,255,255,0.68)",
    fontSize: "15px",
  },

  actions: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
  },

  playButton: {
    width: "48px",
    height: "48px",
    borderRadius: "50%",
    border: "none",
    background: "#d6b56d",
    color: "#050505",
    fontSize: "18px",
    fontWeight: "900",
  },

  downloadButton: {
    width: "46px",
    height: "46px",
    borderRadius: "50%",
    border: "2px solid rgba(214,181,109,0.38)",
    background: "rgba(0,0,0,0.24)",
    color: "#d6b56d",
    fontSize: "22px",
    fontWeight: "900",
  },

  deleteButton: {
    width: "40px",
    height: "40px",
    borderRadius: "50%",
    border: "none",
    background: "rgba(120,20,20,0.85)",
    color: "#ffffff",
    fontSize: "26px",
  },

  player: {
    position: "fixed",
    left: "16px",
    right: "16px",
    bottom: "92px",
    zIndex: 20,
    padding: "13px 14px 11px",
    borderRadius: "22px",
    background: "rgba(0,2,7,0.98)",
    border: "1px solid rgba(214,181,109,0.55)",
  },

  playerMain: {
    display: "flex",
    alignItems: "center",
    gap: "10px",
  },

  playerCover: {
    width: "48px",
    height: "48px",
    minWidth: "48px",
    borderRadius: "12px",
    border: "1px solid rgba(214,181,109,0.7)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },

  playerCoverImg: {
    width: "40px",
    height: "40px",
    objectFit: "contain",
  },

  playerText: {
    flex: 1,
    minWidth: 0,
  },

  playerTitle: {
    color: "#ffffff",
    fontSize: "16px",
    fontWeight: "900",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },

  playerCategory: {
    color: "#d6b56d",
    fontSize: "13px",
    fontWeight: "700",
  },

  playerControls: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
  },

  smallControl: {
    border: "none",
    background: "transparent",
    color: "#ffffff",
    fontSize: "18px",
    fontWeight: "900",
  },

  bigControl: {
    width: "52px",
    height: "52px",
    borderRadius: "50%",
    border: "2px solid rgba(214,181,109,0.9)",
    background: "rgba(0,0,0,0.35)",
    color: "#d6b56d",
    fontSize: "23px",
    fontWeight: "900",
  },

  timeLine: {
    display: "flex",
    justifyContent: "space-between",
    color: "rgba(255,255,255,0.72)",
    fontSize: "12px",
    marginTop: "8px",
  },

  progress: {
    width: "100%",
    accentColor: "#d6b56d",
    marginTop: "4px",
  },
};

export default Music;